// @systeme: interface
// « Ma semaine » (DEC-026) : un regard sur l'organisation, sur demande. Des faits, jamais
// un jugement, et pour chacun un geste à faire d'un clic quand il y en a un. Le silence
// (« rien de particulier ») est une réponse valable. Réservé aux formules qui l'incluent.
import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ChevronLeft } from 'lucide-react';
import { Link as LienRouteur, useNavigate } from 'react-router';
import type { ActionCoaching, BilanSemaine } from '@foyer/shared';
import { Chargement } from '../composants/Chargement';
import { useMessages } from '../composants/Messages';
import { ErreurClient, api } from '../lib/api';
import { formaterJourLong } from '../lib/dates';
import { couleurs, tailles } from '../theme/theme';

export function MaSemaine() {
  const { dire, direErreur } = useMessages();
  const navigate = useNavigate();
  const [bilan, setBilan] = useState<BilanSemaine | undefined>();
  const [indisponible, setIndisponible] = useState<string | undefined>();
  // Recalculé après chaque geste : le bilan change quand l'organisation change.
  const [version, setVersion] = useState(0);

  useEffect(() => {
    api.coaching
      .maSemaine()
      .then(setBilan)
      .catch((e: unknown) => {
        if (e instanceof ErreurClient && e.code === 'coaching_indisponible')
          setIndisponible(e.message);
        else direErreur(e);
      });
  }, [version, direErreur]);
  const charger = useCallback(() => setVersion((v) => v + 1), []);

  async function agir(action: ActionCoaching) {
    try {
      switch (action.type) {
        case 'reporter':
          await api.elements.reporter(action.elementId, {
            quand: action.quand,
            ...(action.date ? { date: action.date } : {}),
          });
          break;
        case 'supprimer':
          await api.elements.supprimer(action.elementId);
          break;
        case 'mettre_en_pause':
          await api.recurrences.modifier(action.recurrenceId, { active: false });
          break;
        case 'changer_jours':
          await api.recurrences.modifier(action.recurrenceId, { regle: action.regle });
          break;
        case 'ouvrir':
          navigate(action.chemin);
          return;
      }
      dire('C’est fait.');
      charger();
    } catch (e) {
      direErreur(e);
    }
  }

  return (
    <Box sx={{ px: `${tailles.marge}px`, pt: 3 }}>
      <Button
        component={LienRouteur}
        to="/moi"
        startIcon={<ChevronLeft size={18} />}
        color="inherit"
        sx={{ px: 0, mb: 1 }}
      >
        Moi
      </Button>
      <Typography variant="h1" component="h1" gutterBottom>
        Ma semaine
      </Typography>
      {indisponible ? (
        <Stack spacing={1.5}>
          <Typography color="text.secondary">{indisponible}</Typography>
          <Button
            component={LienRouteur}
            to="/moi"
            variant="outlined"
            sx={{ alignSelf: 'flex-start' }}
          >
            Voir les formules
          </Button>
        </Stack>
      ) : !bilan ? (
        <Chargement />
      ) : (
        <Stack spacing={2}>
          <Typography color="text.secondary">
            Du {formaterJourLong(bilan.semaineDebut)} au {formaterJourLong(bilan.semaineFin)}.
            {bilan.faitesSeptJours > 0
              ? ` Sur les sept derniers jours : ${bilan.faitesSeptJours} chose${bilan.faitesSeptJours > 1 ? 's' : ''} faite${bilan.faitesSeptJours > 1 ? 's' : ''}.`
              : ''}
          </Typography>
          {bilan.observations.length === 0 && (
            <Typography sx={{ py: 4, textAlign: 'center', fontWeight: 600 }}>
              Rien de particulier cette semaine.
            </Typography>
          )}
          {bilan.observations.map((o, i) => (
            <Paper
              key={`${o.cle}-${i}`}
              variant="outlined"
              sx={{
                p: 2,
                borderColor: 'transparent',
                bgcolor: o.cle === 'respiration' ? couleurs.saugeClair : 'background.paper',
              }}
            >
              <Typography>{o.texte}</Typography>
              {o.actions.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                  {o.actions.map((a, j) => (
                    <Button
                      key={j}
                      size="small"
                      variant={j === 0 && a.type !== 'ouvrir' ? 'outlined' : 'text'}
                      color={a.type === 'supprimer' ? 'inherit' : 'primary'}
                      onClick={() => void agir(a)}
                    >
                      {a.libelle}
                    </Button>
                  ))}
                </Stack>
              )}
            </Paper>
          ))}
          <Typography variant="body2" color="text.secondary">
            Ce regard ne parle que de l’organisation, jamais de toi. Rien n’en sort de l’app, et
            rien ne t’est envoyé : il n’existe que quand tu l’ouvres.
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
